import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Megaphone, Trash2, Plus, Image as ImageIcon, X } from "lucide-react";
import { isImageUrl, normalizeImageUrl, tokenizeBody } from "@/lib/media";

export default function Announcements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const canPost = user.role === "admin" || user.role === "manager";

  const load = () => api.get("/announcements").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    // Mark announcements as seen so the sidebar badge clears.
    api.post("/announcements/mark-read").catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#ffc700]">Company News</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Announcements</h1>
        </div>
        {canPost && (
          <button
            onClick={() => setOpen(true)}
            data-testid="new-announcement"
            className="px-4 py-2.5 text-black rounded-lg font-bold flex items-center gap-2 hover:opacity-90"
            style={{ background: "var(--accent)" }}
          >
            <Plus size={16} /> New Announcement
          </button>
        )}
      </div>

      <div className="space-y-4">
        {items.length === 0 && <div className="text-slate-500 text-sm">No announcements yet.</div>}
        {items.map((a) => {
          const tokens = tokenizeBody(a.body);
          return (
            <article key={a.id} data-testid={`ann-${a.id}`} className="card-surface p-6 relative overflow-hidden">
              {a.priority === "high" && <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#ffc700]" />}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#ffc70020] flex items-center justify-center text-[#ffc700]">
                  <Megaphone size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-bold">{a.title}</h3>
                    <span className={`pill ${a.priority === "high" ? "bg-[#ffc70020] text-[#ffc700]" : "bg-[#242c3d] text-slate-300"}`}>{a.priority}</span>
                    {a.department && <span className="pill bg-[#00e5ff20] text-[#00e5ff]">{a.department}</span>}
                  </div>
                  <div className="text-sm text-slate-300 mt-2 whitespace-pre-wrap leading-relaxed break-words">
                    {tokens.map((t, i) =>
                      t.type === "image" ? (
                        <img
                          key={`${a.id}-tok-${i}`}
                          src={t.url}
                          alt="Shared GIF"
                          loading="lazy"
                          className="my-2 rounded-lg max-h-80"
                        />
                      ) : (
                        <span key={`${a.id}-tok-${i}`}>{t.value}</span>
                      ),
                    )}
                  </div>
                  {a.image_url && isImageUrl(a.image_url) && (
                    <img
                      src={a.image_url}
                      alt={a.title}
                      loading="lazy"
                      className="mt-3 rounded-xl max-h-96 border border-[#2a3447]"
                    />
                  )}
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-3">
                    {new Date(a.created_at).toLocaleString()} · {a.author_name}
                  </div>
                </div>
                {canPost && (
                  <button
                    onClick={async () => { await api.delete(`/announcements/${a.id}`); load(); }}
                    data-testid={`delete-ann-${a.id}`}
                    className="text-slate-500 hover:text-[#ff3b5c]"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {open && <CreateModal onClose={() => setOpen(false)} onCreated={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [dept, setDept] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const preview = normalizeImageUrl(imageUrl.trim());
  const previewOk = isImageUrl(preview);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/announcements", {
      title,
      body,
      priority,
      department: dept || null,
      image_url: previewOk ? preview : null,
    });
    onCreated();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="ann-modal">
        <h2 className="text-xl font-bold mb-4">New Announcement</h2>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" required data-testid="ann-title" className="w-full mb-3 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message (paste a GIF URL to embed it inline)" required rows={5} data-testid="ann-body" className="w-full mb-3 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]" />
        <div className="grid grid-cols-2 gap-3 mb-3">
          <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="ann-priority" className="bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]">
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
          <input value={dept} onChange={(e) => setDept(e.target.value)} placeholder="Department (optional)" data-testid="ann-dept" className="bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]" />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <ImageIcon size={12} /> Cover GIF / image URL
          </label>
          <div className="mt-2 relative">
            <input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="Paste a GIF or image URL (giphy, tenor, .gif, .png…)"
              data-testid="ann-image-url"
              className="w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 pr-10 outline-none focus:border-[color:var(--accent)]"
            />
            {imageUrl && (
              <button type="button" onClick={() => setImageUrl("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#ff3b5c]" data-testid="ann-image-clear">
                <X size={14} />
              </button>
            )}
          </div>
          {previewOk && (
            <img src={preview} alt="preview" className="mt-3 rounded-lg max-h-48 border border-[#2a3447]" data-testid="ann-image-preview" />
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
          <button type="submit" data-testid="ann-submit" className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Publish</button>
        </div>
      </form>
    </div>
  );
}
